<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-container class="file-definition-container">
    <!-- File Definition List -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span class="text-h6">File Definitions</span>
            <div>
              <input
                ref="fileInput"
                type="file"
                accept=".json"
                @change="handleFileUpload"
                style="display: none"
              />
              <v-btn
                small
                color="primary"
                class="mr-2"
                @click="triggerFileUpload"
              >
                <v-icon small left>{{ icons.mdiUpload }}</v-icon>
                Upload Definition
              </v-btn>
              <v-btn
                small
                color="primary"
                @click="openAddDialog"
              >
                <v-icon small left>{{ icons.mdiPlus }}</v-icon>
                Add New Definition
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <!-- Data Table -->
            <v-data-table
              :headers="tableHeaders"
              :items="fileDefinitions"
              :loading="loading"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [5, 10, 20, 50]
              }"
              class="elevation-1"
            >
              <template v-slot:item.Delimiter="{ item }">
                {{ item.FileType === 'Fixed Width' ? '' : formatDelimiter(item.Delimiter) }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  small
                  @click="openEditDialog(item)"
                >
                  <v-icon small>{{ icons.mdiPencil }}</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click="downloadFileDefinitionSpec(item)"
                >
                  <v-icon small>{{ icons.mdiFileDocument }}</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click="downloadFileDefinition(item)"
                >
                  <v-icon small>{{ icons.mdiDownload }}</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="error"
                  @click="confirmDelete(item)"
                >
                  <v-icon small>{{ icons.mdiDelete }}</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add/Edit Dialog -->
    <v-dialog
      v-model="dialogVisible"
      max-width="1200px"
      persistent
    >
      <v-card>
        <v-card-title class="py-2">
          <span class="text-h6">
            {{ isEditing ? 'Edit File Definition' : 'Add File Definition' }}
          </span>
        </v-card-title>
        <v-card-text class="pb-2">
          <v-container class="py-0">
            <!-- Basic Information -->
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.Name"
                  label="Name *"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.ImportExport"
                  :items="importExportOptions"
                  item-text="text"
                  item-value="value"
                  label="Import/Export *"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.Program"
                  :items="programOptions"
                  item-text="text"
                  item-value="value"
                  label="Program"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.FileType"
                  :items="fileTypeOptions"
                  item-text="text"
                  item-value="value"
                  label="File Type *"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="6" v-if="editedItem.FileType === 'CSV'">
                <v-select
                  v-model="editedItem.Delimiter"
                  :items="delimiterOptions"
                  item-text="text"
                  item-value="value"
                  label="Delimiter"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  :value="calculatedTotalColumns"
                  label="Total Columns"
                  outlined
                  dense
                  readonly
                  disabled
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="editedItem.IncludeHeader"
                  label="Include Header"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" md="6" v-if="editedItem.FileType === 'CSV'">
                <v-checkbox
                  v-model="editedItem.QuoteStrings"
                  label="Quote Strings"
                ></v-checkbox>
              </v-col>
            </v-row>

            <!-- Column Definitions Section -->
            <v-row class="mt-2">
              <v-col cols="12" class="py-0">
                <v-divider class="my-2"></v-divider>
                <h3 class="text-subtitle-1 font-weight-bold mb-2">Column Definitions</h3>

                <div v-if="editedItem.ColumnDefinition && editedItem.ColumnDefinition.length > 0">
                  <v-card
                    v-for="(column, index) in sortedColumnDefinitions"
                    :key="index"
                    class="mb-2 column-card"
                    outlined
                    draggable="true"
                    @dragstart="handleDragStart(index, $event)"
                    @dragover.prevent="handleDragOver(index, $event)"
                    @drop="handleDrop(index, $event)"
                    @dragend="handleDragEnd"
                  >
                    <v-card-text class="py-2">
                      <v-row dense>
                        <v-col cols="12" md="4">
                          <v-text-field
                            :value="column.ColumnNumber"
                            label="Column Number"
                            outlined
                            dense
                            readonly
                            disabled
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="column.Name"
                            label="Name"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="column.FieldType"
                            :items="fieldTypeOptions"
                            item-text="text"
                            item-value="value"
                            label="Field Type"
                            outlined
                            dense
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="4" v-if="editedItem.FileType === 'Fixed Width'">
                          <v-text-field
                            v-model.number="column.StartPosition"
                            type="number"
                            label="Start Position"
                            outlined
                            dense
                            @input="handleStartPositionChange(column)"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" v-if="editedItem.FileType === 'Fixed Width'">
                          <v-text-field
                            v-model.number="column.EndPosition"
                            type="number"
                            label="End Position"
                            outlined
                            dense
                            @input="handleEndPositionChange(column)"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" v-if="editedItem.FileType === 'Fixed Width'">
                          <v-text-field
                            v-model.number="column.Length"
                            type="number"
                            label="Length"
                            outlined
                            dense
                            @input="handleLengthChange(column)"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="column.PaddingType"
                            :items="paddingTypeOptions"
                            item-text="text"
                            item-value="value"
                            label="Padding Type"
                            outlined
                            dense
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="column.MapToTable"
                            :items="availableTablesWithSpecialFields"
                            item-text="text"
                            item-value="value"
                            label="Table"
                            outlined
                            dense
                            @change="handleTableSelection(column)"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="column.MapToColumn"
                            :items="column.availableColumns || []"
                            item-text="text"
                            item-value="value"
                            label="Column"
                            outlined
                            dense
                            :disabled="!column.MapToTable"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="4" v-if="column.FieldType === 'Number'">
                          <v-checkbox
                            v-model="column.StripDecimal"
                            label="Strip Decimal"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                      <v-row dense class="mt-0">
                        <v-col cols="12" class="text-right pt-0">
                          <v-btn
                            x-small
                            text
                            color="error"
                            @click="removeColumnDefinition(column)"
                          >
                            <v-icon x-small left>{{ icons.mdiDelete }}</v-icon>
                            Delete
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </div>
                <div v-else class="text-center grey--text py-2">
                  No column definitions added yet
                </div>

                <v-btn
                  small
                  color="primary"
                  @click="addColumnDefinition"
                  class="mt-2"
                >
                  <v-icon small left>{{ icons.mdiPlus }}</v-icon>
                  Add Column
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="py-2">
          <v-spacer></v-spacer>
          <v-btn
            text
            small
            @click="dialogVisible = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            small
            :disabled="!isFormValid"
            @click="saveFileDefinition"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import {
  mdiPencil, mdiDownload, mdiUpload, mdiPlus, mdiDelete, mdiFileDocument,
} from '@mdi/js';
import { clients } from '../../util/clients';

export default {
  name: 'FileDefinitionSetting',
  data() {
    return {
      baseCrudKey: 'file-definitions',
      loading: false,
      dialogVisible: false,
      isEditing: false,
      draggedIndex: null,
      autoScrollInterval: null,
      editedItem: {
        Name: '',
        ImportExport: '',
        Program: '',
        FileType: '',
        Delimiter: '',
        TotalColumns: null,
        IncludeHeader: false,
        QuoteStrings: false,
        ColumnDefinition: [],
      },
      tableHeaders: [
        { text: 'Name', value: 'Name', sortable: true },
        { text: 'Import/Export', value: 'ImportExport', sortable: true },
        { text: 'Program', value: 'Program', sortable: true },
        { text: 'File Type', value: 'FileType', sortable: true },
        { text: 'Delimiter', value: 'Delimiter', sortable: false },
        { text: 'Total Columns', value: 'TotalColumns', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      importExportOptions: [
        { text: 'Import', value: 'Import' },
        { text: 'Export', value: 'Export' },
      ],
      fileTypeOptions: [
        { text: 'CSV', value: 'CSV' },
        { text: 'Fixed Width', value: 'Fixed Width' },
      ],
      delimiterOptions: [
        { text: 'Comma (,)', value: ',' },
        { text: 'Pipe (|)', value: '|' },
        { text: 'Tab', value: '\t' },
        { text: 'Semicolon (;)', value: ';' },
      ],
      paddingTypeOptions: [
        { text: 'Left Space', value: 'Left Space' },
        { text: 'Left Zero', value: 'Left Zero' },
        { text: 'Right Space', value: 'Right Space' },
        { text: 'Right Zero', value: 'Right Zero' },
        { text: 'None', value: 'None' },
      ],
      fieldTypeOptions: [
        { text: 'String', value: 'String' },
        { text: 'Number', value: 'Number' },
        { text: 'Date', value: 'Date' },
        { text: 'Boolean', value: 'Boolean' },
      ],
      programOptions: [],
      columnsByTable: {},
      icons: {
        mdiPencil,
        mdiDownload,
        mdiUpload,
        mdiPlus,
        mdiDelete,
        mdiFileDocument,
      },
    };
  },
  computed: {
    ...mapState('config', ['availableTables', 'tableColumns']),
    ...mapGetters('base/crud', ['items']),
    fileDefinitions() {
      return this.items(this.baseCrudKey) || [];
    },
    availableTablesWithSpecialFields() {
      const tables = (this.availableTables || []).map((table) => ({
        text: table,
        value: table,
      }));
      // Add "Special Fields" option from lists
      tables.push({
        text: 'Special Fields',
        value: 'Special Fields',
      });
      return tables;
    },
    calculatedTotalColumns() {
      if (!this.editedItem.ColumnDefinition || this.editedItem.ColumnDefinition.length === 0) {
        return 0;
      }

      if (this.editedItem.FileType === 'CSV') {
        // For CSV: max Column Number
        const maxColumnNumber = Math.max(
          ...this.editedItem.ColumnDefinition.map((col) => col.ColumnNumber || 0),
        );
        return maxColumnNumber;
      }
      if (this.editedItem.FileType === 'Fixed Width') {
        // For Fixed Width: max End Position
        const maxEndPosition = Math.max(
          ...this.editedItem.ColumnDefinition.map((col) => col.EndPosition || 0),
        );
        return maxEndPosition;
      }

      return 0;
    },
    sortedColumnDefinitions() {
      if (!this.editedItem.ColumnDefinition) return [];
      return [...this.editedItem.ColumnDefinition].sort((a, b) => (
        (a.ColumnNumber || 0) - (b.ColumnNumber || 0)
      ));
    },
    isFormValid() {
      return this.editedItem.Name
             && this.editedItem.ImportExport
             && this.editedItem.FileType;
    },
  },
  mounted() {
    this.loadTables();
    this.loadProgramOptions();
    this.loadData();
  },
  methods: {
    ...mapActions('config', [
      'loadFileDefinitions',
      'upsertFileDefinition',
      'deleteFileDefinition',
      'loadAvailableTables',
      'loadTableColumns',
    ]),
    ...mapActions('lists', [
      'loadListByName',
    ]),
    ...mapActions([
      'flashSuccess',
      'flashError',
    ]),
    formatDelimiter(delimiter) {
      const delimiterMap = {
        ',': 'Comma (,)',
        '|': 'Pipe (|)',
        '\t': 'Tab',
        ';': 'Semicolon (;)',
      };
      return delimiterMap[delimiter] || delimiter;
    },
    async loadTables() {
      try {
        await this.loadAvailableTables({ baseCrudKey: this.baseCrudKey });
        // Note: availableTables is now in Vuex state, accessed via computed property
      } catch (error) {
        console.error('Error loading tables:', error);
      }
    },
    async loadProgramOptions() {
      try {
        const programList = await this.loadListByName({ name: 'File Import/Export Programs' });
        if (programList && programList.DataDefinition) {
          this.programOptions = programList.DataDefinition
            .filter((item) => !item.Removed)
            .map((item) => ({
              text: item.LongName || item.ShortName,
              value: item.ShortName,
            }));
        } else {
          this.programOptions = [];
        }
      } catch (error) {
        console.error('Error loading program options:', error);
        this.programOptions = [];
      }
    },
    async loadColumnsForTable(tableName) {
      if (!tableName) return [];

      // Check if already cached in Vuex
      if (this.tableColumns[tableName]) {
        return this.tableColumns[tableName].map((col) => ({
          text: col,
          value: col,
        }));
      }

      try {
        // Special handling for "Special Fields" - load from lists
        if (tableName === 'Special Fields') {
          const list = await this.loadListByName({ name: 'Special Fields' });
          if (list && list.DataDefinition) {
            const columnOptions = list.DataDefinition
              .filter((item) => !item.Removed)
              .map((item) => ({
                text: item.LongName || item.ShortName,
                value: item.ShortName,
              }));
            return columnOptions;
          }
          return [];
        }

        // Regular table columns from database schema
        const columns = await this.loadTableColumns({ tableName });
        return (columns || []).map((col) => ({
          text: col,
          value: col,
        }));
      } catch (error) {
        console.error('Error loading columns:', error);
        return [];
      }
    },
    async handleTableSelection(column) {
      const updatedColumn = column;
      if (updatedColumn.MapToTable) {
        updatedColumn.availableColumns = await this.loadColumnsForTable(
          updatedColumn.MapToTable,
        );
      } else {
        updatedColumn.availableColumns = [];
        updatedColumn.MapToColumn = '';
      }
    },
    async loadData() {
      this.loading = true;
      try {
        await this.loadFileDefinitions({
          criteria: {},
          baseCrudKey: this.baseCrudKey,
        });
      } catch (error) {
        this.flashError('Failed to load file definitions');
        console.error('Error loading file definitions:', error);
      } finally {
        this.loading = false;
      }
    },
    openAddDialog() {
      this.isEditing = false;
      this.editedItem = {
        Name: '',
        ImportExport: '',
        Program: '',
        FileType: '',
        Delimiter: '',
        TotalColumns: null,
        IncludeHeader: false,
        QuoteStrings: false,
        ColumnDefinition: [],
      };
      this.dialogVisible = true;
    },
    async openEditDialog(item) {
      this.isEditing = true;
      this.editedItem = {
        ...item,
        QuoteStrings: item.QuoteStrings !== undefined ? item.QuoteStrings : false,
        ColumnDefinition: item.ColumnDefinition
          ? JSON.parse(JSON.stringify(item.ColumnDefinition))
          : [],
      };

      // Load columns for existing mapped tables and calculate/populate all fields
      if (this.editedItem.ColumnDefinition) {
        for (let i = 0; i < this.editedItem.ColumnDefinition.length; i += 1) {
          const column = this.editedItem.ColumnDefinition[i];
          if (column.MapToTable) {
            const columns = await this.loadColumnsForTable(column.MapToTable);
            this.$set(this.editedItem.ColumnDefinition[i], 'availableColumns', columns);
          } else {
            this.$set(this.editedItem.ColumnDefinition[i], 'availableColumns', []);
          }

          // For Fixed Width files, ensure all position fields are populated
          if (this.editedItem.FileType === 'Fixed Width') {
            // If we have start and end, calculate length
            if (column.StartPosition && column.EndPosition && !column.Length) {
              // eslint-disable-next-line no-param-reassign
              column.Length = column.EndPosition - column.StartPosition + 1;
            } else if (column.StartPosition && column.Length && !column.EndPosition) {
              // If we have start and length, calculate end
              // eslint-disable-next-line no-param-reassign
              column.EndPosition = column.StartPosition + column.Length - 1;
            } else if (column.EndPosition && column.Length && !column.StartPosition) {
              // If we have end and length, calculate start
              // eslint-disable-next-line no-param-reassign
              column.StartPosition = column.EndPosition - column.Length + 1;
            } else if (!column.Length) {
              // If length is missing and we can't calculate it, default to 10
              // eslint-disable-next-line no-param-reassign
              column.Length = 10;
              // If we have start position, calculate end position
              if (column.StartPosition) {
                // eslint-disable-next-line no-param-reassign
                column.EndPosition = column.StartPosition + column.Length - 1;
              }
            }
          }
        }
      }

      this.dialogVisible = true;
    },
    addColumnDefinition() {
      if (!this.editedItem.ColumnDefinition) {
        this.editedItem.ColumnDefinition = [];
      }

      // Calculate starting position for Fixed Width files
      let startPosition = null;
      if (this.editedItem.FileType === 'Fixed Width'
          && this.editedItem.ColumnDefinition.length > 0) {
        // Find the maximum end position from existing columns
        const maxEndPosition = Math.max(
          ...this.editedItem.ColumnDefinition.map((col) => col.EndPosition || 0),
        );
        startPosition = maxEndPosition > 0 ? maxEndPosition + 1 : 1;
      }

      this.editedItem.ColumnDefinition.push({
        ColumnNumber: this.editedItem.ColumnDefinition.length + 1,
        Name: '',
        StartPosition: startPosition,
        EndPosition: null,
        Length: null,
        PaddingType: 'None',
        StripDecimal: false,
        FieldType: 'String',
        MapToTable: '',
        MapToColumn: '',
        availableColumns: [],
      });
    },
    updateSubsequentColumns(changedColumn) {
      if (!this.editedItem.ColumnDefinition || this.editedItem.FileType !== 'Fixed Width') {
        return;
      }

      // Find the index of the changed column
      const changedIndex = this.editedItem.ColumnDefinition.findIndex(
        (col) => col === changedColumn,
      );
      if (changedIndex === -1 || !changedColumn.EndPosition) return;

      // Update all columns after this one
      const sortedColumns = [...this.editedItem.ColumnDefinition].sort(
        (a, b) => (a.ColumnNumber || 0) - (b.ColumnNumber || 0),
      );

      const changedColumnNumber = changedColumn.ColumnNumber;
      let previousEndPosition = changedColumn.EndPosition;

      for (const col of sortedColumns) {
        if (col.ColumnNumber > changedColumnNumber) {
          // Update this column's start position to be 1 more than previous end
          // eslint-disable-next-line no-param-reassign
          col.StartPosition = previousEndPosition + 1;

          // Recalculate end position based on length
          if (col.Length) {
            // eslint-disable-next-line no-param-reassign
            col.EndPosition = col.StartPosition + col.Length - 1;
          } else if (col.EndPosition) {
            // If end position exists but length doesn't, recalculate length
            // eslint-disable-next-line no-param-reassign
            col.Length = col.EndPosition - col.StartPosition + 1;
          }

          previousEndPosition = col.EndPosition || previousEndPosition;
        }
      }
    },
    handleStartPositionChange(column) {
      const updatedColumn = column;
      if (updatedColumn.StartPosition && updatedColumn.Length) {
        updatedColumn.EndPosition = updatedColumn.StartPosition + updatedColumn.Length - 1;
        this.updateSubsequentColumns(updatedColumn);
      } else if (updatedColumn.StartPosition && updatedColumn.EndPosition) {
        updatedColumn.Length = updatedColumn.EndPosition - updatedColumn.StartPosition + 1;
        this.updateSubsequentColumns(updatedColumn);
      }
    },
    handleEndPositionChange(column) {
      const updatedColumn = column;
      if (updatedColumn.StartPosition && updatedColumn.EndPosition) {
        updatedColumn.Length = updatedColumn.EndPosition - updatedColumn.StartPosition + 1;
        this.updateSubsequentColumns(updatedColumn);
      }
    },
    handleLengthChange(column) {
      const updatedColumn = column;
      if (updatedColumn.StartPosition && updatedColumn.Length) {
        updatedColumn.EndPosition = updatedColumn.StartPosition + updatedColumn.Length - 1;
        this.updateSubsequentColumns(updatedColumn);
      }
    },
    removeColumnDefinition(column) {
      const index = this.editedItem.ColumnDefinition.findIndex((col) => col === column);
      if (index !== -1) {
        this.editedItem.ColumnDefinition.splice(index, 1);

        // Renumber remaining columns to maintain sequential order
        this.editedItem.ColumnDefinition.forEach((col, idx) => {
          // eslint-disable-next-line no-param-reassign
          col.ColumnNumber = idx + 1;
        });
      }
    },
    handleDragStart(index, event) {
      this.draggedIndex = index;
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.effectAllowed = 'move';
    },
    handleDragOver(index, event) {
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.dropEffect = 'move';
    },
    handleDrop(dropIndex, event) {
      // eslint-disable-next-line no-param-reassign
      event.preventDefault();

      if (this.draggedIndex === null || this.draggedIndex === dropIndex) return;

      // Get the sorted array
      const sortedArray = [...this.sortedColumnDefinitions];

      // Remove dragged item and insert at new position
      const [draggedItem] = sortedArray.splice(this.draggedIndex, 1);
      sortedArray.splice(dropIndex, 0, draggedItem);

      // Update column numbers based on new order
      sortedArray.forEach((col, idx) => {
        // eslint-disable-next-line no-param-reassign
        col.ColumnNumber = idx + 1;
      });

      // Replace the original array with reordered items
      this.editedItem.ColumnDefinition = sortedArray;
    },
    handleDragEnd() {
      this.draggedIndex = null;
    },
    async saveFileDefinition() {
      try {
        const item = { ...this.editedItem };
        delete item.__typename;

        // Set TotalColumns from computed value
        item.TotalColumns = this.calculatedTotalColumns;

        // Clean up temporary UI properties from column definitions
        if (item.ColumnDefinition) {
          item.ColumnDefinition = item.ColumnDefinition.map((col) => {
            const cleanCol = { ...col };
            delete cleanCol.availableColumns;
            delete cleanCol.__typename;
            return cleanCol;
          });
        }

        await this.upsertFileDefinition({
          item,
          baseCrudKey: this.baseCrudKey,
        });
        const message = this.isEditing
          ? 'File definition updated successfully'
          : 'File definition created successfully';
        this.flashSuccess(message);
        this.dialogVisible = false;
        await this.loadData();
      } catch (error) {
        this.flashError('Failed to save file definition');
        console.error('Error saving file definition:', error);
      }
    },
    confirmDelete(item) {
      if (window.confirm(`Are you sure you want to delete "${item.Name}"?`)) {
        this.deleteItem(item);
      }
    },
    async deleteItem(item) {
      try {
        await this.deleteFileDefinition({
          _id: item._id,
          baseCrudKey: this.baseCrudKey,
        });
        this.flashSuccess('File definition deleted successfully');
        await this.loadData();
      } catch (error) {
        this.flashError('Failed to delete file definition');
        console.error('Error deleting file definition:', error);
      }
    },
    downloadFileDefinition(item) {
      try {
        // Create a clean copy without UI-specific properties
        const cleanItem = { ...item };
        delete cleanItem.__typename;

        if (cleanItem.ColumnDefinition) {
          cleanItem.ColumnDefinition = cleanItem.ColumnDefinition.map((col) => {
            const cleanCol = { ...col };
            delete cleanCol.__typename;
            delete cleanCol.availableColumns;
            return cleanCol;
          });
        }

        // Convert to JSON
        const jsonString = JSON.stringify(cleanItem, null, 2);

        // Create blob and download
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${item.Name.replace(/[^a-z0-9]/gi, '_')}_definition.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        this.flashSuccess('File definition downloaded successfully');
      } catch (error) {
        this.flashError('Failed to download file definition');
        console.error('Error downloading file definition:', error);
      }
    },
    async downloadFileDefinitionSpec(item) {
      try {
        const { backendRest } = clients.direct;
        const response = await backendRest.get(
          `/fileDefinition/download/${item._id}`,
          {
            responseType: 'blob',
          },
        );

        const filename = `${item.Name.replace(/[^a-z0-9]/gi, '_')}_spec.txt`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.flashSuccess('File definition specification downloaded successfully');
      } catch (error) {
        this.flashError('Failed to download file definition specification');
        console.error('Error downloading file definition specification:', error);
      }
    },
    triggerFileUpload() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    async handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      try {
        const fileContent = await file.text();
        const uploadedDefinition = JSON.parse(fileContent);

        // Remove _id to create a new definition instead of updating existing
        delete uploadedDefinition._id;

        // Validate required fields
        if (!uploadedDefinition.Name
            || !uploadedDefinition.ImportExport
            || !uploadedDefinition.FileType) {
          this.flashError('Invalid file definition: missing required fields');
          return;
        }

        // Open edit dialog with uploaded data
        this.isEditing = false;
        this.editedItem = {
          ...uploadedDefinition,
          ColumnDefinition: uploadedDefinition.ColumnDefinition || [],
        };

        // Load columns for existing mapped tables and calculate/populate all fields
        if (this.editedItem.ColumnDefinition) {
          for (const column of this.editedItem.ColumnDefinition) {
            if (column.MapToTable) {
              // eslint-disable-next-line no-param-reassign
              column.availableColumns = await this.loadColumnsForTable(column.MapToTable);
            } else {
              // eslint-disable-next-line no-param-reassign
              column.availableColumns = [];
            }

            // For Fixed Width files, ensure all position fields are populated
            if (this.editedItem.FileType === 'Fixed Width') {
              // If we have start and end, calculate length
              if (column.StartPosition && column.EndPosition && !column.Length) {
                // eslint-disable-next-line no-param-reassign
                column.Length = column.EndPosition - column.StartPosition + 1;
              } else if (column.StartPosition && column.Length && !column.EndPosition) {
                // If we have start and length, calculate end
                // eslint-disable-next-line no-param-reassign
                column.EndPosition = column.StartPosition + column.Length - 1;
              } else if (column.EndPosition && column.Length && !column.StartPosition) {
                // If we have end and length, calculate start
                // eslint-disable-next-line no-param-reassign
                column.StartPosition = column.EndPosition - column.Length + 1;
              } else if (!column.Length) {
                // If length is missing and we can't calculate it, default to 10
                // eslint-disable-next-line no-param-reassign
                column.Length = 10;
                // If we have start position, calculate end position
                if (column.StartPosition) {
                  // eslint-disable-next-line no-param-reassign
                  column.EndPosition = column.StartPosition + column.Length - 1;
                }
              }
            }
          }
        }

        this.dialogVisible = true;
        this.flashSuccess('File definition loaded successfully. Review and save to create.');
      } catch (error) {
        this.flashError('Failed to parse file definition. Please ensure it is a valid JSON file.');
        console.error('Error parsing file definition:', error);
      } finally {
        // Reset file input
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }
      }
    },
  },
};
</script>

<style scoped>
.file-definition-container {
  padding: 1rem;
}

.column-card {
  cursor: move;
  background-color: #fafafa;
}

.column-card:hover {
  background-color: #f5f5f5;
}
</style>
